/**
 * LeaseMutateDrawer - Sheet/Dialog wrapper for LeaseForm
 *
 * Handles: chrome (Sheet or Dialog), data fetching, mutations, discard dialog.
 * Toggle button lets user switch between side-panel and modal mode.
 * Form fields are rendered by LeaseForm.
 */

import { useEffect, useMemo, useState } from 'react'
import { PanelRight, Maximize2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useLeaseById,
  useContractAircraft,
  useCreateLease,
  useUpdateLease,
} from '@/hooks/useLeases'
import { LeaseForm } from './LeaseForm'
import type { SelectedAircraft } from './LeaseForm'
import { buildRenewalDefaultsFromRow } from './lease-form-utils'
import type { LeaseFormInput, LeaseFormValues } from './lease-form-schema'
import type { LeaseFormData } from '@/types/leases'

type DrawerMode = 'sidePanel' | 'modal'

/** Pre-fill data extracted from the current allocation context (aircraft card). */
export interface LeasePrefillData {
  unit_id?: string
  client_id?: string
  contact_id?: string
  aircraft?: SelectedAircraft[]
}

interface LeaseMutateDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Lease ID for editing an existing lease */
  leaseId?: string
  /** Lease ID to base a renewal on — fetches the lease and computes renewal defaults */
  renewalLeaseId?: string
  /** Pre-computed form defaults for renewal (alternative to renewalLeaseId) */
  renewalDefaults?: LeaseFormInput | null
  /** Lock location select (pre-filled, disabled). Used when opened from ramp view. */
  lockedLocationId?: string
  /** Pre-fill form fields from allocation context (unit, client, aircraft). */
  prefillData?: LeasePrefillData
}

export function LeaseMutateDrawer({
  open,
  onOpenChange,
  leaseId,
  renewalLeaseId,
  renewalDefaults: externalRenewalDefaults,
  lockedLocationId,
  prefillData,
}: LeaseMutateDrawerProps) {
  const isEditing = !!leaseId

  // Fetch existing lease (edit mode) or renewal source lease
  const fetchLeaseId = leaseId ?? renewalLeaseId
  const { data: fetchedLease, isLoading: isLoadingLease } =
    useLeaseById(fetchLeaseId)
  const { data: contractAircraft } = useContractAircraft(leaseId ?? renewalLeaseId)

  // Compute renewal defaults from fetched lease (when renewalLeaseId is used)
  const computedRenewalDefaults = useMemo(() => {
    if (externalRenewalDefaults) return externalRenewalDefaults
    if (renewalLeaseId && fetchedLease) return buildRenewalDefaultsFromRow(fetchedLease)
    return null
  }, [externalRenewalDefaults, renewalLeaseId, fetchedLease])

  const isRenewal = !isEditing && !!computedRenewalDefaults

  // Mutations
  const createLease = useCreateLease()
  const updateLease = useUpdateLease()

  // State
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('sidePanel')

  // Map contract aircraft to the shape LeaseForm expects
  const [mappedAircraft, setMappedAircraft] = useState<SelectedAircraft[]>([])

  useEffect(() => {
    // Only sync API-fetched aircraft in edit/renewal mode
    if (!fetchLeaseId) return
    if (contractAircraft && contractAircraft.length > 0) {
      setMappedAircraft(
        contractAircraft.map((ca) => ({
          id: ca.aircraft_id,
          tailNumber: ca.tail_number || '',
          typeName: ca.aircraft_type_name,
          isPrimary: ca.is_primary,
        }))
      )
    } else {
      setMappedAircraft([])
    }
  }, [contractAircraft, fetchLeaseId])

  // Reset when creating fresh (not editing, not renewal)
  // Pre-populate aircraft from prefillData if available
  useEffect(() => {
    if (!isEditing && !renewalLeaseId && open) {
      setMappedAircraft(prefillData?.aircraft ?? [])
      setFormDirty(false)
    }
  }, [isEditing, renewalLeaseId, open, prefillData])

  // Handle close with dirty check
  const handleClose = () => {
    if (formDirty) {
      setShowDiscardDialog(true)
    } else {
      onOpenChange(false)
    }
  }

  const handleDiscard = () => {
    setShowDiscardDialog(false)
    setFormDirty(false)
    onOpenChange(false)
  }

  // Form submission handler (receives parsed data from LeaseForm)
  const handleSubmit = async (
    values: LeaseFormValues,
    aircraft: SelectedAircraft[]
  ) => {
    const aircraftIds = aircraft.map((a) => a.id)
    const primaryAircraftId =
      aircraft.find((a) => a.isPrimary)?.id ?? null

    const formData: LeaseFormData = {
      ...values,
      contact_id: values.contact_id || null,
      end_date: values.end_date || null,
      term_months: values.term_months || null,
      rate_basis: values.rate_basis || null,
      security_deposit: values.security_deposit || null,
      escalation_type: values.escalation_type || null,
      escalation_pct: values.escalation_pct || null,
      escalation_date: values.escalation_date || null,
      renewal_notice_days: values.renewal_notice_days || null,
      transient_revenue_share_pct:
        values.transient_revenue_share_pct || null,
      fuel_type: values.fuel_type || null,
      fuel_commitment_gallons_monthly: values.fuel_commitment_gallons_monthly ?? null,
      fuel_commitment_gallons_annual: values.fuel_commitment_gallons_annual ?? null,
      fuel_price_per_gallon: values.fuel_price_per_gallon ?? null,
      fuel_price_type: values.fuel_price_type || null,
      fuel_price_differential: values.fuel_price_differential ?? null,
      document_url: values.document_url || null,
      signed_at: values.signed_at || null,
      notes: values.notes || null,
    }

    if (isEditing && leaseId) {
      await updateLease.mutateAsync({
        id: leaseId,
        data: formData,
        aircraftIds,
        primaryAircraftId,
      })
    } else {
      await createLease.mutateAsync({
        data: formData,
        aircraftIds,
        primaryAircraftId,
      })
    }

    setFormDirty(false)
    onOpenChange(false)
  }

  const isSubmitting = createLease.isPending || updateLease.isPending
  const isLoading = isLoadingLease && (isEditing || !!renewalLeaseId)

  const title = isEditing
    ? 'Edit Lease Contract'
    : isRenewal
      ? 'Renew Lease Contract'
      : 'Add Lease Contract'

  const description = isEditing
    ? 'Update the lease contract details below.'
    : isRenewal
      ? 'Review and adjust the renewal details below.'
      : 'Enter the lease contract details below.'

  const submitLabel = isSubmitting
    ? 'Saving...'
    : isEditing
      ? 'Save Changes'
      : isRenewal
        ? 'Create Renewal'
        : 'Create Lease'

  const toggleModeButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => setDrawerMode(drawerMode === 'sidePanel' ? 'modal' : 'sidePanel')}
        >
          {drawerMode === 'sidePanel' ? (
            <Maximize2 className="w-4 h-4" />
          ) : (
            <PanelRight className="w-4 h-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {drawerMode === 'sidePanel' ? 'Switch to modal' : 'Switch to side panel'}
      </TooltipContent>
    </Tooltip>
  )

  const formElement = (
    <LeaseForm
      lease={isEditing ? fetchedLease : undefined}
      renewalDefaults={isRenewal ? computedRenewalDefaults : undefined}
      formId="lease-form"
      isEditing={isEditing}
      isLoading={isLoading}
      disabled={isSubmitting}
      onSubmit={handleSubmit}
      contractAircraft={mappedAircraft}
      lockedLocationId={lockedLocationId}
      prefillData={!isEditing && !isRenewal ? prefillData : undefined}
    />
  )

  const footerButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="lease-form"
        disabled={isSubmitting}
      >
        {submitLabel}
      </Button>
    </>
  )

  const discardDialog = (
    <AlertDialog
      open={showDiscardDialog}
      onOpenChange={setShowDiscardDialog}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Are you sure you want to discard
            them?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={handleDiscard}>
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  if (drawerMode === 'modal') {
    return (
      <>
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent className="flex flex-col sm:max-w-[700px] max-h-[90vh] p-0 overflow-hidden">
            <DialogHeader className="px-6 py-4 border-b shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </div>
                {toggleModeButton}
              </div>
            </DialogHeader>

            <ScrollArea className="flex-1 min-h-0">
              {formElement}
            </ScrollArea>

            <DialogFooter className="flex-row justify-end gap-2 px-6 py-4 border-t bg-background shrink-0">
              {footerButtons}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {discardDialog}
      </>
    )
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="flex flex-col w-full sm:max-w-xl p-0 overflow-hidden"
          onInteractOutside={(e) => {
            if (formDirty) e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            if (formDirty) {
              e.preventDefault()
              setShowDiscardDialog(true)
            }
          }}
        >
          <SheetHeader className="px-4 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle>{title}</SheetTitle>
                <SheetDescription>{description}</SheetDescription>
              </div>
              {toggleModeButton}
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1 min-h-0">
            {formElement}
          </ScrollArea>

          <SheetFooter className="flex-row justify-end gap-2 px-4 py-4 border-t bg-background shrink-0">
            {footerButtons}
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  )
}
